import { useContext, forwardRef, type MouseEvent } from 'react';
import type { Config } from '../core';
import { getLocaleUrl } from '../NextgenRouter/routerWithEvents';
import { useNextgenRouter } from '../NextgenRouter/NextgenRouter';
import { events } from '../NextgenRouter/EventEmitter';
import type { NextgenRouterContextInterface } from '../NextgenRouter/types/propsType';
import { useConfig } from '../Config/ConfigProvider';
import { Anchor, LinkContext, LinkProvider } from './LinkProvider';
import { type GenericLinkProps, type Url } from './types/propsTypes';
import styles from './GenericLink.module.scss';

const getHrefString = (href: Url) => (typeof href === 'string' ? href : (href?.href as string));

const onClickExecution = (
  e: MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>,
  router: NextgenRouterContextInterface,
  configs: Config,
  { href, target, replace, onClick }: GenericLinkProps,
) => {
  if (events.getIsBlockRoute()) {
    e.preventDefault();
  }
  const linkHref = getLocaleUrl(href, router.params, configs.NEXT_PUBLIC_LANGUAGE as string);
  const linkHrefUrl = getHrefString(href);
  const isStartsWithHash = linkHrefUrl.startsWith('#');
  if (linkHref && linkHrefUrl !== router.pathname && !e.ctrlKey && target !== '_blank' && href && !isStartsWithHash) {
    const passUrl = `${getHrefString(linkHref)}`;
    if (events.getIsBlockRoute()) {
      events.emit('routeChangeWarn', () => {
        if (replace) {
          router.replace(passUrl);
        } else {
          router.push(passUrl);
        }
      });
    } else {
      events.emit('routeChangeStart', linkHref);
    }
  }
  if (onClick) {
    onClick(e);
  }
};

const GenericLink = forwardRef<HTMLAnchorElement, GenericLinkProps>(
  ({ forceNative = false, href, forwardedRef, ...restProps }: GenericLinkProps, ref) => {
    const { isNext14, element: NextJsLink } = useContext(LinkContext);
    const router = useNextgenRouter();
    const configs = useConfig();

    const onClickWithEvents = (e: MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>) => {
      onClickExecution(e, router, configs, { href, ...restProps });
    };

    return (
      <>
        {NextJsLink && !forceNative ? (
          <NextJsLink
            href={isNext14 ? href : getLocaleUrl(href, router.params, configs.NEXT_PUBLIC_LANGUAGE as string)}
            {...restProps}
            onClick={onClickWithEvents}
            ref={ref || forwardedRef}
            className={`${styles['generic-link-decoration']} ${restProps.className}`}
          />
        ) : (
          <Anchor
            href={
              isNext14
                ? getHrefString(href)
                : (getLocaleUrl(getHrefString(href), router.params, configs.NEXT_PUBLIC_LANGUAGE as string) as string)
            }
            {...restProps}
            onClick={onClickWithEvents}
            ref={ref || forwardedRef}
            className={`${styles['generic-link-decoration']} ${restProps.className}`}
          />
        )}
      </>
    );
  },
);
GenericLink.displayName = 'GenericLink';

export { GenericLink, LinkProvider };
export type { GenericLinkProps };
export default GenericLink;
